import { canonicalIds, readKey, sharePendingRead } from './sharedRead';
import axios from "axios";
import { createReadCache } from './readCache';
import { Dispatch } from "redux";
import { fetchAuthSession } from "aws-amplify/auth";
import { refresh_stories } from "../store/appSlice";
import { readSavedStudioChapters } from './studioSavedChapters';
import { usesBetaApi } from '../config/backendEnvironment';

const PROD_ENDPOINT = "4471m6hcxd.execute-api.us-east-1.amazonaws.com";
const BETA_API_BASE_URL = "https://0orfuu32nl.execute-api.us-east-1.amazonaws.com/prod";
const BETA_USAGE_API_BASE_URL = "https://uu65fefr5m.execute-api.us-east-1.amazonaws.com/prod";
const BETA_WEBSOCKET_V2 = "wss://cpdeqvw762.execute-api.us-east-1.amazonaws.com/test-v2/";
const FABLE_ENGINE_API_BASE_URL = "https://engine-api.thefableapp.com";
const FABLE_ENGINE_WEBSOCKET_V2 = "wss://engine-api.thefableapp.com/realtime";
const PROD_WEBSOCKET_V2 =
  "wss://swr052yf81.execute-api.us-east-1.amazonaws.com/prod-v2/";
const FORCE_FABLE_ENGINE_API = true;
const VOICE_CATALOG_VERSION = 'horizon-v2' as const;

const LAMBDA_API_BASE_URL = `https://${PROD_ENDPOINT}/prod`;

// Keep story/media requests and realtime in their app environment. Real account
// purchases use live billing in both environments; sandbox payment endpoints
// remain available for explicit provider integration tests.
// Never let a checked-in development flag redirect the production website.
type BackendRouting = {
  apiMode: "lambda" | "ecs";
  lambdaApiBaseUrl: string;
  lambdaWebSocketV2Url: string;
  fableEngineApiBaseUrl: string;
  fableEngineRealtimeUrl: string;
};

const ENGINE_ROUTES = new Set([
  "/gambit",
  "/usage",
  "/usage/balance",
  "/usage/pricing",
  "/stories",
  "/stories-by-story-ids",
  "/create-story-v2",
  "/studio-story",
  "/gambit/stories/search",
  "/create-next-chapter-v2",
  "/create-video-v2",
  "/get-story-recommendation",
  "/get-analytics",
  "/edit-story",
  "/edit-story-v2",
  "/story-version",
  "/commit-story-edit-v2",
  "/discard-story-edit-image",
  "/delete-story",
  "/delete-chapter",
  "/story-deletion-context",
  "/rate-story",
  "/get-characters",
  "/save-character",
  "/delete-character",
  "/characters/design-preview",
  "/design-voice",
  "/create-voice",
  "/get-voice",
  "/delete-voice",
  "/send-support-email",
  "/save-notifications-settings",
  "/get-notifications-settings",
  "/stripe/create-checkout-session",
  "/stripe/create-portal-session",
  "/stripe/end-trial",
  "/book-order/generate-cover",
  "/book-order/my-covers",
  "/book-order/get-story-images",
  "/book-order/get-pricing",
  "/book-order/create-checkout",
  "/book-order/preview-pdf",
  "/book-order/preview-pdf-status",
  "/book-order/my-orders",
  "/book-order/v2/catalog",
  "/book-order/v2/back-cover-copy",
  "/book-order/v2/cover-designs",
  "/book-order/v2/manifest",
  "/book-order/v2/quote",
  "/book-order/v2/drafts",
  "/social/accounts",
  "/social/share",
  "/affiliate/link",
  "/affiliate/bind",
  "/affiliate/rewards",
  "/affiliate/claim",
  "/affiliate/program",
]);

const ENGINE_ROUTE_PREFIXES = [
  "/book-order/cover-status/",
  "/book-order/v2/drafts/",
  "/social/accounts/",
  "/social/facebook/",
  "/social/instagram/",
  "/social/tiktok/",
  "/social/youtube/",
];

let backendRouting: BackendRouting = {
  apiMode: FORCE_FABLE_ENGINE_API ? "ecs" : "lambda",
  lambdaApiBaseUrl: LAMBDA_API_BASE_URL,
  lambdaWebSocketV2Url: PROD_WEBSOCKET_V2,
  fableEngineApiBaseUrl: FABLE_ENGINE_API_BASE_URL,
  fableEngineRealtimeUrl: FABLE_ENGINE_WEBSOCKET_V2,
};

const normalizeRoute = (route: string): string =>
  route.startsWith("/") ? route : `/${route}`;

export const applyBackendRoutingFromFeatureGates = (_response: any): void => {
  // Fable Web is intentionally pinned to bundled routing defaults for now.
  // Feature gates still provide product/config data, but cannot flip Web
  // between Lambda and ECS at runtime.
};

export const getApiBaseUrl = (route?: string, hostname = window.location.hostname): string => {
  const normalizedRoute = route
    ? normalizeRoute(route).split("?")[0]
    : undefined;
  if (normalizedRoute && ["/stripe/create-checkout-session", "/stripe/create-portal-session", "/stripe/end-trial"].includes(normalizedRoute)) {
    return FABLE_ENGINE_API_BASE_URL;
  }
  if (usesBetaApi(hostname)) {
    // Usage/Gambit have their own authenticated beta gateway. Classic uses
    // the test stack's API Gateway because there is no beta Engine service.
    return normalizedRoute === "/gambit" || normalizedRoute === "/usage" || normalizedRoute?.startsWith("/usage/")
      ? BETA_USAGE_API_BASE_URL
      : BETA_API_BASE_URL;
  }
  const routeCanUseEngine = normalizedRoute
    ? ENGINE_ROUTES.has(normalizedRoute) ||
      ENGINE_ROUTE_PREFIXES.some((prefix) => normalizedRoute.startsWith(prefix))
    : true;
  return FORCE_FABLE_ENGINE_API &&
    backendRouting.apiMode === "ecs" &&
    routeCanUseEngine
    ? backendRouting.fableEngineApiBaseUrl
    : backendRouting.lambdaApiBaseUrl;
};

export const apiUrl = (route: string): string =>
  `${getApiBaseUrl(route)}${normalizeRoute(route)}`;
const featureGateUrl = (route: string): string =>
  `${getApiBaseUrl(route)}${normalizeRoute(route)}`;

export const getWebSocketEndpointV2 = (hostname = window.location.hostname): string =>
  usesBetaApi(hostname) ? BETA_WEBSOCKET_V2 : FORCE_FABLE_ENGINE_API && backendRouting.apiMode === "ecs"
    ? backendRouting.fableEngineRealtimeUrl
    : backendRouting.lambdaWebSocketV2Url;

/**
 * Helper function to get auth headers with JWT token
 */
export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (idToken) {
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      };
    }

    // No token available, return basic headers
    return {
      "Content-Type": "application/json",
    };
  } catch (error) {
    console.error("Error getting auth token:", error);
    // Fall back to basic headers if not authenticated
    return {
      "Content-Type": "application/json",
    };
  }
};

export const recordSignupTerms = async (intent: {
  version: string;
  source: 'web';
  startedAt: string;
}) => axios.put(apiUrl('/signup-terms'), intent, {
  headers: await getAuthHeaders(),
  timeout: 15000,
});

export type StoryLibrarySortOrder = "newest" | "oldest";
export type StoryLibraryMode = "Regular" | "Dialogue" | "Musical";
export type StoryLibraryLength = "Normal" | "Long";
export type StoryLibraryReader = "Toddler" | "Teen" | "Adult";

export interface StoryLibraryQuery {
  sortOrder?: StoryLibrarySortOrder;
  storyModes: StoryLibraryMode[];
  storyLengths: StoryLibraryLength[];
  storyReaders: StoryLibraryReader[];
}

export interface GetStoriesInput {
  userId?: string;
  lastEvaluatedKey?: any;
  summaryOnly?: boolean;
  sortOrder?: StoryLibrarySortOrder;
  storyModes?: StoryLibraryMode[];
  storyLengths?: StoryLibraryLength[];
  storyReaders?: StoryLibraryReader[];
}

export interface GetStoriesByStoryIdsInput {
  userId: string;
  storyIds: string[];
}

export interface EditStoryImagesInput {
  userId: string;
  story: any;
  imageEdits: {
    imageIndex: number;
    prompt: string;
    maskDataUrl?: string;
    maskUrl?: string;
  }[];
  sourceStoryVersion?: number;
  supportsImageEditDrafts?: boolean;
  overwriteImageEditDraft?: boolean;
  appendImageEditDraft?: boolean;
  editPricingModel?: "legacyFlat" | "perImage";
}

export interface GetStoryVersionInput {
  userId: string;
  storyId: string;
  storyVersion: number;
  includeBuilding?: boolean;
}

export interface GetStoryVersionResponse {
  storyId: string;
  storyVersion: number;
  sourceStoryVersion?: number;
  isActive: boolean;
  createdAt?: string;
  completedAt?: string;
  status?: "BUILDING" | "COMPLETE";
  isDraft?: boolean;
  draftReadyAt?: string;
  draftImageIndexes?: number[];
  activeDraftImageIndexes?: number[];
  completedDraftImageIndexes?: number[];
  discardedImageIndexes?: number[];
  draftJobStatus?: string;
  media?: Record<string, unknown>;
  story: any;
}

export interface CommitStoryEditV2Input {
  userId: string;
  storyId: string;
  storyVersion?: number;
}

export interface CommitStoryEditV2Response {
  storyId: string;
  storyVersion: number;
  jobId?: string;
  scheduled?: boolean;
  message?: string;
}

export interface DiscardStoryEditImageInput {
  userId: string;
  storyId: string;
  storyVersion?: number;
  imageIndex: number;
}

export interface DiscardStoryEditImageResponse {
  storyId: string;
  storyVersion: number;
  imageIndex: number;
  imageKey?: string;
  imageUrl?: string;
  draftReady?: boolean;
  draftDiscarded?: boolean;
}

export interface UpdateStoryTitlesInput {
  userId: string;
  storyId: string;
  storyTitle: string;
  chapterTitles: {
    storyId: string;
    title: string;
    chapterNumber?: number;
  }[];
}

export interface CreateEditMaskUploadUrlInput {
  userId: string;
  storyId: string;
  imageIndex: number;
  contentType?: string;
}

export interface CreateEditMaskUploadUrlResponse {
  uploadUrl: string;
  maskUrl: string;
  key: string;
  contentType: string;
  uploadHeaders?: Record<string, string>;
  expiresAt: string;
}

export interface SendMailInput {
  message?: string;
  userId?: string;
  email?: string;
}

export interface SaveNotificationSettingsInput {
  userId?: string;
  storyUpdates?: boolean;
  creationUpdates?: boolean;
  bookUpdates?: boolean;
  fableUpdates?: boolean;
  reminders?: boolean;
}

export interface GetNotificationSettingsInput {
  userId?: string;
  isGlobalNotificationsOn?: boolean;
}

export type StudioStorySearchPage = { stories: Array<{
  storyId: string; storyName: string; rootStoryTitle: string; chapterIds: string[]; createDate?: number;
  pending: boolean; failed: boolean; pendingChapterCount: number;
  matches: Array<{ storyId: string; title: string; chapterNumber: number; mode: 'classic' | 'gambit';
    pending: boolean; incomplete: boolean; pendingVideoCreation: boolean; pendingFFmpeg: boolean }>;
}>; nextCursor?: string; timedOut?: boolean };

export async function searchStudioStories(query: string, cursor?: string, signal?: AbortSignal): Promise<StudioStorySearchPage> {
  const headers = await getAuthHeaders();
  if (!headers.Authorization) throw Object.assign(new Error('Sign in to search your saved stories.'), { status: 401 });
  const response = await axios.post(apiUrl('/gambit/stories/search'), { query, complete: true, ...(cursor ? { cursor } : {}) },
    { headers, signal, timeout: 10000 });
  return response.data;
}

const fetchStoriesPageFromService = async (body: GetStoriesInput) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl(`/stories`), body, { headers });
    return {
      stories: [...response.data.stories],
      lastEvaluatedKey: response.data.lastEvaluatedKey,
      totalCount: response.data.totalCount,
    };
  } catch (error) {
    console.error("Error fetching stories:", error);
    throw error;
  }
};

// Share identical reads while they are in flight (including Strict Mode
// remounts), without caching a response across explicit refreshes or mutations.
const storyPageRequests = new Map<string, ReturnType<typeof fetchStoriesPageFromService>>();
export const fetchStoriesPage = (body: GetStoriesInput) => {
  if (!body.userId) return fetchStoriesPageFromService(body);
  const key = JSON.stringify([apiUrl('/stories'), Object.entries(body)
    .filter(([, value]) => value !== undefined).sort(([a], [b]) => a.localeCompare(b))]);
  const existing = storyPageRequests.get(key);
  if (existing) return existing;
  const request = fetchStoriesPageFromService(body).finally(() => { storyPageRequests.delete(key); });
  storyPageRequests.set(key, request);
  return request;
};

export const fetchStories = async (
  dispatch: Dispatch,
  body: GetStoriesInput,
  stories?: any[],
) => {
  try {
    const page = await fetchStoriesPage(body);
    const orderedStories = page.stories;
    if (!stories || stories.length === 0) {
      dispatch(
        refresh_stories({
          stories: orderedStories,
          last_refreshed_at: Date.now(),
          last_story_edited_at: null,
        }),
      );
    } else {
      dispatch(
        refresh_stories({
          stories: [...stories, ...orderedStories],
          last_refreshed_at: Date.now(),
          last_story_edited_at: null,
        }),
      );
    }
    return page.lastEvaluatedKey;
  } catch (error) {
    console.error("Error fetching stories:", error);
    throw error;
  }
};

const fetchStoriesByStoryIdsFromService = async (
  body: GetStoriesByStoryIdsInput,
) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl(`/stories-by-story-ids`), body, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching stories by story IDs:", error);
    throw error;
  }
};

/** Owner-only metadata read, available on the beta Lambda API and Engine. */
export const getStoryDeletionContext = async (userId: string, storyId: string) => {
  const headers = await getAuthHeaders();
  const response = await axios.post(apiUrl('/story-deletion-context'), { userId, storyId }, { headers });
  return response.data;
};

export const getAnalyticsData = async (userId: string) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      apiUrl(`/get-analytics`),
      { userId },
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error getting analytics data:", error);
    throw error;
  }
};

export const editStoryImages = async (
  body: EditStoryImagesInput,
  useV2 = false,
): Promise<any> => {
  try {
    const endpoint = useV2 ? "/edit-story-v2" : "/edit-story";
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl(endpoint), body, { headers });
    return response.data;
  } catch (error) {
    console.error("Error editing story images:", error);
    throw error;
  }
};

export const fetchStoryVersion = async (
  body: GetStoryVersionInput,
): Promise<GetStoryVersionResponse> => {
  const headers = await getAuthHeaders();
  const response = await axios.post(apiUrl("/story-version"), body, {
    headers,
  });
  return response.data;
};

export const commitStoryEditV2 = async (
  body: CommitStoryEditV2Input,
): Promise<CommitStoryEditV2Response> => {
  const headers = await getAuthHeaders();
  const response = await axios.post(apiUrl("/commit-story-edit-v2"), body, {
    headers,
  });
  return response.data;
};

export const discardStoryEditImage = async (
  body: DiscardStoryEditImageInput,
): Promise<DiscardStoryEditImageResponse> => {
  const headers = await getAuthHeaders();
  const response = await axios.post(apiUrl("/discard-story-edit-image"), body, {
    headers,
  });
  return response.data;
};

export const updateStoryTitles = async (
  body: UpdateStoryTitlesInput,
): Promise<any> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl("/update-story-titles"), body, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating story titles:", error);
    throw error;
  }
};

export const createEditMaskUploadUrl = async (
  body: CreateEditMaskUploadUrlInput,
): Promise<CreateEditMaskUploadUrlResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      apiUrl("/edit-story-mask-upload-url"),
      body,
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error creating edit mask upload URL:", error);
    throw error;
  }
};

export const deleteStory = async (
  dispatch: Dispatch,
  body: any,
  stories: any[],
) => {
  const { storyId } = body;
  try {
    const headers = await getAuthHeaders();
    await axios.post(apiUrl(`/delete-story`), body, {
      headers,
    });
    const updatedStories = stories.filter((story) => story.storyId !== storyId);
    dispatch(
      refresh_stories({
        stories: updatedStories,
        last_refreshed_at: Date.now(),
        last_story_edited_at: null,
      }),
    );
  } catch (error) {
    console.error("Error deleting story:", error);
    throw error;
  }
};

export interface DeleteStoryInput {
  storyId: string;
  userId: string;
}

export interface RateStoryInput {
  userId: string;
  storyId: string;
  rating: number;
  feedback?: string;
}

export interface CreateVideoStoryInput {
  userId: string;
  storyId: string;
  token: string;
  deviceType: string;
  storyMode?: string;
  videoMode?: "regular" | "premium" | "hollywood" | string;
  storyLength?: string;
}

/**
 * Pure API function to delete a story (no Redux dispatch)
 * Use this with TanStack Query mutations
 */
export const deleteChapterApi = async (body: { userId: string; storyId: string; chapterId: string }): Promise<{ storyId: string | null; chapterIds: string[]; storyDeleted: boolean; chapters: { storyId: string; chapterNumber: number }[] }> => {
  const headers = await getAuthHeaders();
  const response = await axios.post(apiUrl('/delete-chapter'), body, { headers, timeout: 120000 });
  return response.data;
};

export const deleteStoryApi = async (body: DeleteStoryInput) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl(`/delete-story`), body, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting story:", error);
    throw error;
  }
};

export const rateStoryApi = async (body: RateStoryInput) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl("/rate-story"), body, { headers });
    return response.data;
  } catch (error) {
    console.error("Error rating story:", error);
    throw error;
  }
};

export const createVideoStory = async (body: CreateVideoStoryInput) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl("/create-video-v2"), body, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating video story:", error);
    throw error;
  }
};

export const sendMail = async (body: SendMailInput) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl(`/send-support-email`), body, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error sending mail:", error);
    throw error;
  }
};

export const saveNotificationSettings = async (
  body: SaveNotificationSettingsInput,
) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      apiUrl(`/save-notifications-settings`),
      body,
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error saving notification settings:", error);
    throw error;
  }
};

export const getNotificationSettings = async (
  body: GetNotificationSettingsInput,
) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      apiUrl(`/get-notifications-settings`),
      body,
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error getting notification settings:", error);
    throw error;
  }
};

// ─── Create + Characters API ────────────────────────────────────────────────

export interface FableCharacter {
  characterId?: string;
  id?: string;
  characterName?: string;
  name?: string;
  kind?: string | null;
  gender?: string | null;
  images?: string[] | null;
  referenceImages?: string[] | null;
  imageMode?: "reference" | "super" | null;
  age?: string | number | null;
  hairColor?: string | null;
  hairLength?: string | null;
  eyeColor?: string | null;
  skinTone?: string | null;
  voiceId?: string | null;
  description?: string | null;
  preventImageUpload?: boolean;
  clearReferenceImages?: boolean;
  finishedTraining?: boolean | null;
  requestId?: string | null;
  pending?: boolean;
  status?: "pending" | "generating" | "complete" | "completed" | "failed";
  generationStatus?:
    "pending" | "generating" | "complete" | "completed" | "failed";
  progress?: number;
  trainingProgress?: number;
  errorMessage?: string;
  loraUrl?: string | null;
  designPreviewImageKey?: string | null;
  designPreviewImageUrl?: string | null;
  designPrompt?: string | null;
}

export interface CharacterSummary {
  characterId: string;
  characterName: string;
  finishedTraining?: boolean | null;
  requestId?: string | null;
  pending?: boolean;
  status?: "pending" | "generating" | "complete" | "completed" | "failed";
  generationStatus?:
    "pending" | "generating" | "complete" | "completed" | "failed";
  progress?: number;
}

export type CharacterKindFilter = "human" | "non-human";

export interface GetCharactersInput {
  userId: string;
  pageSize?: number;
  nextToken?: string;
  kindFilter?: CharacterKindFilter;
  characterId?: string;
  characterIds?: string[];
  includeSummaries?: boolean;
}

export interface GetCharactersResponse {
  characters: FableCharacter[];
  characterSummaries?: CharacterSummary[];
  activeCharacterIds?: string[];
  nextToken?: string;
  hasMore?: boolean;
}

export interface DesignVoiceInput {
  userId: string;
  voiceCatalogVersion?: typeof VOICE_CATALOG_VERSION;
  /** Optional spoken language/region, such as en-GB. Omit to infer from the description. */
  languageCode?: string;
  async?: boolean;
  updateExistingVoice?: boolean;
  generatedVoiceId?: string;
  voiceName?: string;
  voiceDescription?: string;
  pickForMe?: boolean;
  gender: "male" | "female" | "neutral";
  avatarImageBase64?: string;
  avatarImageUrl?: string;
}

export interface CreateVoiceInput {
  userId: string;
  async?: boolean;
  voiceName: string;
  voiceDescription: string;
  creationMethod?: "design" | "clone";
  voicePrompt?: string;
  previewText?: string;
  avatarPrompt?: string;
  avatarUrl?: string | null;
  avatarImageBase64?: string;
  gender?: "male" | "female" | "neutral";
  generatedVoiceId: string;
  previewAudioBase64?: string;
  mediaType?: string;
  waveform?: number[];
  chargeTokensOnSave?: boolean;
  updateExistingVoice?: boolean;
}

export interface DeleteVoiceInput {
  userId: string;
  voiceId: string;
}

export interface VoiceRecord {
  userId: string;
  voiceId: string;
  languageCode?: string;
  gender: "male" | "female" | "neutral";
  creationMethod?: "design" | "clone";
  status?: "pending" | "generating" | "complete" | "failed";
  generationStatus?: "pending" | "generating" | "complete" | "failed";
  pending?: boolean;
  progress?: number;
  requestId?: string;
  errorMessage?: string;
  voiceName: string;
  voiceDescription: string;
  generatedDescription?: string;
  voicePrompt?: string;
  previewText?: string;
  waveform?: number[];
  previewUrl: string;
  referenceUrl?: string;
  signedPreviewUrl?: string;
  avatarUrl?: string | null;
  signedAvatarUrl?: string;
  avatarPrompt?: string;
  provider?: "seed-audio" | "elevenlabs" | "fable";
  durationSecs?: number;
  previewDurationSecs?: number;
  duration_seconds?: number;
  createdAt: number;
  updatedAt: number;
}

export interface CharacterDesignPreviewInput {
  userId: string;
  characterName?: string;
  description?: string;
  designPrompt: string;
  kind?: string | null;
  gender?: string | null;
  ageVibe?: string | null;
  storyStyle?: string | null;
  outfit?: string | null;
  personality?: string | null;
  colorPalette?: string | null;
  referenceImageBase64?: string | null;
  async?: boolean;
  includeVoice?: boolean;
  voiceDescription?: string;
  generatedCharacterId?: string;
  matchingVoiceId?: string | null;
}

export interface CharacterDesignPreviewResponse {
  previewImageUrl: string;
  previewImageKey: string;
  cdnUrl?: string;
  structuredFields: FableCharacter;
  character?: FableCharacter;
  success?: boolean;
  pending?: boolean;
  characterId?: string;
  generatedCharacterId?: string;
  matchingVoiceId?: string;
  voiceId?: string;
  characterPrompt: string;
  tokens?: number;
  cost?: number;
  notEnoughTokens?: boolean;
  message?: string;
}

const getCharactersFromService = async (
  body: GetCharactersInput,
): Promise<GetCharactersResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl("/get-characters"), body, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error getting characters:", error);
    throw error;
  }
};

export const saveCharacter = async (body: {
  userId: string;
  character: FableCharacter;
}) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl("/save-character"), body, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error saving character:", error);
    throw error;
  }
};

export const deleteCharacterApi = async (body: {
  userId: string;
  characterId: string;
}) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl("/delete-character"), body, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting character:", error);
    throw error;
  }
};

export const designCharacterPreview = async (
  body: CharacterDesignPreviewInput,
): Promise<CharacterDesignPreviewResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      apiUrl("/characters/design-preview"),
      body,
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error generating character design preview:", error);
    throw error;
  }
};

export const designVoice = async (body: DesignVoiceInput): Promise<any> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl("/design-voice"), {
      ...body,
      voiceCatalogVersion: VOICE_CATALOG_VERSION,
    }, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error designing voice:", error);
    throw error;
  }
};

export const createVoice = async (body: CreateVoiceInput): Promise<any> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl("/create-voice"), body, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating voice:", error);
    throw error;
  }
};

export interface GetVoicesPageInput {
  userId: string;
  pageSize?: number;
  nextToken?: string;
  creationMethod?: "design" | "clone";
}

export interface GetVoicesPageResponse {
  voices: VoiceRecord[];
  activeVoiceIds?: string[];
  nextToken?: string;
  hasMore?: boolean;
}

const getVoicesPageFromService = async (
  input: GetVoicesPageInput,
): Promise<GetVoicesPageResponse> => {
  try {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams({ userId: input.userId });
    params.set('voiceCatalogVersion', VOICE_CATALOG_VERSION);
    if (input.pageSize) params.set("pageSize", String(input.pageSize));
    if (input.nextToken) params.set("nextToken", input.nextToken);
    if (input.creationMethod)
      params.set("creationMethod", input.creationMethod);
    const response = await axios.get(
      apiUrl(`/get-voice?${params.toString()}`),
      { headers },
    );
    return {
      voices: response.data.voices || [],
      activeVoiceIds: Array.isArray(response.data.activeVoiceIds)
        ? response.data.activeVoiceIds
        : undefined,
      nextToken: response.data.nextToken || undefined,
      hasMore: response.data.hasMore ?? Boolean(response.data.nextToken),
    };
  } catch (error) {
    console.error("Error getting voice page:", error);
    throw error;
  }
};

export const deleteVoiceApi = async (body: DeleteVoiceInput): Promise<any> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.delete(
      apiUrl(
        `/delete-voice?userId=${encodeURIComponent(body.userId)}&voiceId=${encodeURIComponent(body.voiceId)}`,
      ),
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting voice:", error);
    throw error;
  }
};

export const createStoryV2 = async (body: Record<string, unknown>) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl("/create-story-v2"), {
      ...body,
      voiceCatalogVersion: VOICE_CATALOG_VERSION,
    }, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating story:", error);
    throw error;
  }
};

export const createNextChapterV2 = async (body: Record<string, unknown>) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl("/create-next-chapter-v2"), {
      ...body,
      voiceCatalogVersion: VOICE_CATALOG_VERSION,
    }, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating next chapter:", error);
    throw error;
  }
};

export interface GetStoryRecommendationInput {
  userId: string;
  language?: string | null;
  creativePath?: string;
  creativePathLabel?: string;
  characterIds?: string[];
  storyPreferences?: Record<string, unknown>;
}

export const getStoryRecommendation = async (
  body: GetStoryRecommendationInput,
): Promise<any> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      apiUrl("/get-story-recommendation"),
      body,
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error getting story recommendation:", error);
    throw error;
  }
};

// ─── Book Order API ──────────────────────────────────────────────────────────

export interface GenerateBookCoverInput {
  userId: string;
  storyIds: string[];
  bookTitle: string;
  author?: string;
  coverType: "ai" | "classic";
  aspectRatio?: CoverAspectRatio;
  bookFormatId?: BookFormatIdV2;
  assetPurpose?: CoverAssetPurpose;
  font?: string;
  style?: string;
  customPrompt?: string;
  quality?: "high" | "veryHigh";
  quantity?: number;
  characters?: {
    characterId: string;
    characterName: string;
    imageUrl?: string;
  }[];
}

export type CoverAspectRatio = "portrait" | "square" | "landscape";
export type CoverAssetPurpose = "cover-art" | "thumbnail";
export type BookFormatIdV2 =
  | "novella"
  | "us-trade"
  | "square"
  | "landscape"
  | "us-letter"
  | "us-letter-landscape";

export interface GenerateBookCoverResponse {
  jobId: string;
  status: "generating" | "completed" | "failed";
  aspectRatio?: CoverAspectRatio;
  bookFormatId?: BookFormatIdV2;
  assetPurpose?: CoverAssetPurpose;
  message?: string;
}

export interface CoverStatusResponse {
  jobId: string;
  status: "generating" | "completed" | "failed";
  coverType: "ai" | "classic";
  coverUrl?: string | null;
  coverOptions?: string[] | null;
  quality?: "high" | "veryHigh";
  quantity?: number;
  aspectRatio?: CoverAspectRatio;
  bookFormatId?: BookFormatIdV2;
  assetPurpose?: CoverAssetPurpose;
  error?: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface BookOrderPricingInput {
  userId: string;
  storyIds: string[];
  binding: string;
  color: string;
  finish: string;
  shippingAddress?: {
    street1: string;
    street2?: string;
    city: string;
    state_code: string;
    postcode: string;
    country_code: string;
  };
  countryCode?: string;
}

export interface BookOrderPricing {
  luluBookPrice: number;
  luluShippingPrice: number;
  fableBookPrice: number;
  discount: number;
  finalBookPrice: number;
  shippingPrice: number;
  totalPrice: number;
  currency: string;
  quantity?: number;
}

export interface ShippingOption {
  option: string;
  label: string;
  days: string;
  cost: number;
}

export interface BookOrderPricingResponse {
  pricing: BookOrderPricing;
  shippingOptions: ShippingOption[];
  pageCount: number;
  podPackageId: string;
}

export interface BookOrderPreviewPdfInput {
  userId: string;
  storyIds: string[];
  coverImageUrl: string;
  bookTitle: string;
  dedicationNote?: string;
  colorOption?: "BW" | "Color";
}

export interface ShippingAddress {
  name: string;
  street1: string;
  street2?: string;
  city: string;
  state_code: string;
  postcode: string;
  country_code: string;
  phone_number: string;
}

export interface BookOrderCheckoutInput {
  userId: string;
  userEmail?: string;
  storyIds: string[];
  storyNames: string[];
  bookTitle?: string;
  coverImageUrl: string;
  coverGenerationType?: string;
  coverTokensCost?: number;
  bookOptions: {
    binding: string;
    color: string;
    finish: string;
    bindingId?: string;
    formatId?: string;
    layoutId?: string;
    interiorPrint?: string;
    linenColor?: string;
    foilColor?: string;
    foilStampText?: string;
    coverAssetUrl?: string;
    coverCrop?: {
      x: number;
      y: number;
      zoom: number;
      mode?: "fit" | "fill" | "custom";
    };
    coverBackgroundMode?: "color" | "full-artwork";
    coverDesignId?: string;
    coverDesignAssetUrl?: string;
    coverDesignSourceFingerprint?: string;
    coverDesignPlacement?: {
      widthPixels: number;
      heightPixels: number;
      sourceX: number;
      sourceY: number;
      sourceWidthPixels: number;
      sourceHeightPixels: number;
    };
    coverBackgroundColor?: string;
    resolvedCoverBackgroundColor?: string;
    author?: string;
    openingNote?: string;
    includeChapterPages?: boolean;
    includePageNumbers?: boolean;
    chapterTitleMode?: "none" | "story-title" | "story-and-chapter";
    pageNumberMode?: "none" | "odd-pages" | "all-pages";
    artworkBackgroundMode?: "none" | "blurred" | "cover";
    pagePrintMode?: "double-sided" | "single-sided";
    textSize?: "extra-small" | "small" | "standard" | "large" | "extra-large";
    fontFamily?:
      | "fredoka"
      | "open-sans"
      | "montserrat"
      | "nunito"
      | "lora"
      | "merriweather"
      | "libre-baskerville"
      | "playfair-display"
      | "eb-garamond"
      | "atkinson-hyperlegible";
    backCoverBlurbs?: Array<{ id: string; title: string; description: string }>;
    backCoverTitle?: string;
    backCoverBlurb?: string;
    backCoverLocale?: string;
    podPackageId?: string;
  };
  pricing: BookOrderPricing;
  quantity?: number;
  dedicationNote?: string;
  shippingAddress?: ShippingAddress;
  shippingOption?: string;
  bookBuilderVersion?: 2;
  draft?: unknown;
  manifest?: unknown;
  podPackageId?: string;
}

export interface BookOrder {
  orderId: string;
  bookTitle?: string;
  storyNames: string[];
  coverImageUrl: string;
  status: string;
  luluTrackingUrl?: string;
  estimatedDeliveryMin?: string;
  estimatedDeliveryMax?: string;
  carrierName?: string;
  pricing: {
    totalPrice: number;
    currency: string;
  };
  createDate: number;
  bookOptions: {
    color: string;
    binding: string;
    finish: string;
    formatId?: string;
  };
  shippingOption?: string;
  quantity?: number;
  refunded?: boolean;
  shippingAddress?: {
    name: string;
    street1: string;
    street2?: string;
    city: string;
    state_code: string;
    postcode: string;
    country_code: string;
  };
}

export const generateBookCover = async (
  body: GenerateBookCoverInput,
): Promise<GenerateBookCoverResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      apiUrl(`/book-order/generate-cover`),
      body,
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error generating book cover:", error);
    throw error;
  }
};

export const getCoverStatus = async (
  jobId: string,
): Promise<CoverStatusResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      apiUrl(`/book-order/cover-status/${jobId}`),
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error getting cover status:", error);
    throw error;
  }
};

export const pollForCoverCompletion = async (
  jobId: string,
  intervalMs = 2000,
  maxAttempts = 60,
): Promise<CoverStatusResponse> => {
  for (let i = 0; i < maxAttempts; i++) {
    const result = await getCoverStatus(jobId);
    if (result.status === "completed" || result.status === "failed") {
      return result;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("Cover generation timed out");
};

export const getBookOrderPricing = async (
  body: BookOrderPricingInput,
): Promise<BookOrderPricingResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl(`/book-order/get-pricing`), body, {
      headers,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching book order pricing:", error);
    throw error;
  }
};

export const getBookOrderPreviewPdf = async (
  body: BookOrderPreviewPdfInput,
) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(apiUrl(`/book-order/preview-pdf`), body, {
      headers,
    });
    return response.data as { jobId: string; status: string };
  } catch (error) {
    console.error("Error generating preview PDF:", error);
    throw error;
  }
};

export interface PreviewPdfStatusResponse {
  jobId: string;
  status: "generating" | "completed" | "failed";
  pdfUrl: string | null;
  pageCount: number | null;
  storyCount: number | null;
  pageThumbnails: string[] | null;
  error: string | null;
}

export const getPreviewPdfStatus = async (
  jobId: string,
): Promise<PreviewPdfStatusResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      apiUrl(`/book-order/preview-pdf-status?jobId=${jobId}`),
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error getting preview PDF status:", error);
    throw error;
  }
};

export const createBookOrderCheckout = async (body: BookOrderCheckoutInput) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      apiUrl(`/book-order/create-checkout`),
      body,
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error creating book order checkout:", error);
    throw error;
  }
};

export const getMyBookOrders = async (userId: string) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      apiUrl(`/book-order/my-orders?userId=${userId}`),
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching book orders:", error);
    throw error;
  }
};

export const getStoryImages = async (userId: string, storyIds: string[]) => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      apiUrl(`/book-order/get-story-images`),
      { userId, storyIds },
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching story images:", error);
    throw error;
  }
};

export const FEATURE_GATES_CACHE_MS = 10 * 60 * 1000;
const featureGatesCache = createReadCache<any>(FEATURE_GATES_CACHE_MS);
export const clearFeatureGatesCache = featureGatesCache.clear;
export const getFeatureGates = (userId: string, options: { force?: boolean } = {}) => featureGatesCache.read(
  JSON.stringify([featureGateUrl('/get-feature-gates'), userId]), async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      featureGateUrl("/get-feature-gates"),
      { userId },
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching feature gates:", error);
    throw error;
  }
}, options);

export const getUserCovers = async (
  userId: string,
  aspectRatio?: CoverAspectRatio,
  assetPurpose?: CoverAssetPurpose,
  storyId?: string,
): Promise<{
  covers: string[];
  bookTitle?: string;
  coverAspectRatioMap?: Record<string, CoverAspectRatio>;
  coverFormatMap?: Record<string, BookFormatIdV2>;
  assetPurpose?: CoverAssetPurpose | null;
  storyId?: string | null;
}> => {
  try {
    const headers = await getAuthHeaders();
    const query = new URLSearchParams({ userId });
    if (aspectRatio) query.set("aspectRatio", aspectRatio);
    if (assetPurpose) query.set("assetPurpose", assetPurpose);
    if (storyId) query.set("storyId", storyId);
    const response = await axios.get(
      apiUrl(`/book-order/my-covers?${query.toString()}`),
      { headers },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching user covers:", error);
    throw error;
  }
};

export const getUserThumbnails = async (userId: string, storyId: string) =>
  getUserCovers(userId, undefined, "thumbnail", storyId);

/** Approved Studio assets are published through the authenticated story backend. */
export const studioStoryRequest = async (body: Record<string, unknown>) => {
  if (body.operation === 'chapters') {
    return readSavedStudioChapters(String(body.userId || ''), String(body.storyId || ''), String(body.chapterId || body.storyId || ''), fetchStoriesByStoryIds);
  }
  if (body.operation === 'status' && body.storyId) {
    const userId = String(body.userId || ''), storyId = String(body.storyId);
    if (!userId) throw new Error('Sign in to view your story.');
    const response = await fetchStoriesByStoryIds({ userId, storyIds: [storyId] });
    const story = response.stories?.find((item: any) => item.storyId === storyId && (!item.userId || item.userId === userId));
    if (!story || story.chapterDeletionLock || story.error) throw new Error('This story is unavailable. Please try again.');
    return { storyId, pending: !!story.pending, failed: !!story.failed,
      pendingVideoCreation: !!story.pendingVideoCreation, pendingFFmpeg: !!story.pendingFFmpeg,
      pendingLandscape: !!story.pendingLandscape, pendingSubtitles: !!story.pendingSubtitles,
      pendingOperationType: story.pendingOperationType,
      imageVideoUrl: story.imageVideoUrl, animatedVideoUrl: story.animatedVideoUrl,
      storyMode: story.storyMode, storyLength: story.storyLength };
  }
  const headers = await getAuthHeaders();
  const response = await axios.post(apiUrl('/studio-story'), body, { headers, timeout: 120000 });
  return response.data;
};

export const fetchStoriesByStoryIds = (body: GetStoriesByStoryIdsInput) => sharePendingRead(readKey('story-details', { ...body, storyIds: canonicalIds(body.storyIds) }), () => fetchStoriesByStoryIdsFromService(body));

export const getCharacters = (body: GetCharactersInput) => sharePendingRead(readKey('characters', { ...body, ...(body.characterIds ? { characterIds: canonicalIds(body.characterIds) } : {}) }), () => getCharactersFromService(body));

export const getVoicesPage = (input: GetVoicesPageInput) => sharePendingRead(readKey('voices', { ...input }), () => getVoicesPageFromService(input));

export const getVoices = async (userId: string): Promise<VoiceRecord[]> => (await getVoicesPage({ userId })).voices;
